import React from 'react';
import DataGrid, { Scrolling, Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import TimeTaxSupportSystemClient from '../../../../../system';
import MessageCourier from '../../../../../lib/MessageCourier';
import { ExcelData } from '../../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';
import { Spinner } from '@fluentui/react';
import { t } from 'i18next';

/* columns response:
[
  "UUID",
  "EJERCICIO",
  "PERIODO",
  "COMPANIA",
  "COMPANIA_",
  "Doc_Identidad",
  "Persona",
  "DETALLE",
  "HORAS",
  "IMPORTE_DE_HRS_SIN_MARGEN",
  "IMPORTE_DE_HRS_CON_MARGEN",
  "IMPORTE_DE_GASTOS_SIN_MARGEN",
  "IMPORTE_DE_GASTOS_CON_MARGEN",
  "IMPORTE_TOTAL_SIN_MARGEN",
  "IMPORTE_TOTAL_CON_MARGEN",
  "MARGEN_APLICADO_PORCENTAJE"
]
*/

const allowedColumns = [
  'EJERCICIO',
  'PERIODO',
  'COMPANIA',
  'DETALLE',
  'HORAS',
  'IMPORTE_DE_HRS_SIN_MARGEN',
  'IMPORTE_DE_HRS_CON_MARGEN',
  'IMPORTE_DE_GASTOS_SIN_MARGEN',
  'IMPORTE_DE_GASTOS_CON_MARGEN',
  'IMPORTE_TOTAL_SIN_MARGEN',
  'IMPORTE_TOTAL_CON_MARGEN',
];

const columnCaptionMap: Record<string, string> = {
  EJERCICIO: 'Ejercicio',
  PERIODO: 'Periodo',
  COMPANIA: 'Compañía',
  DETALLE: 'Detalle',
  HORAS: 'Horas',
  IMPORTE_DE_HRS_SIN_MARGEN: 'Importe de Hrs sin Margen',
  IMPORTE_DE_HRS_CON_MARGEN: 'Importe de Hrs con Margen',
  IMPORTE_DE_GASTOS_SIN_MARGEN: 'Importe de Gastos sin Margen',
  IMPORTE_DE_GASTOS_CON_MARGEN: 'Importe de Gastos con Margen',
  IMPORTE_TOTAL_SIN_MARGEN: 'Importe Total sin Margen',
  IMPORTE_TOTAL_CON_MARGEN: 'Importe Total con Margen',
};

interface SettlementTablePreliquidadoAjeProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
  id: string;
}

export const SettlementTablePreliquidadoAje = (props: SettlementTablePreliquidadoAjeProps) => {
  const [data, setData] = React.useState<ExcelData>({ columns: [], rows: [] });
  const [loading, setLoading] = React.useState(true);

  const normalRows = React.useMemo(() => data.rows.filter((r) => r.UUID !== 'TOTALES'), [data.rows]);

  const visibleColumns = React.useMemo(() => data.columns.filter((col) => allowedColumns.includes(col)), [data.columns]);

  const totalsRow = React.useMemo(() => data.rows.find((r) => r.UUID === 'TOTALES'), [data.rows]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await props.system.getLiquidacionAsincronaV2System().getDetallePreliquidacionAje(props.id);
        if (response.data) {
          setData(response.data);
        } else {
          props.courier.messageError(t('No se pudo obtener el detalle.'));
        }
      } catch (error) {
        props.courier.messageError(t('Error al cargar el detalle.'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  if (loading) {
    return <Spinner label={t('Cargando detalle...')} />;
  }

  if (data.rows.length === 0) {
    return <div>{t('No hay datos para mostrar')}</div>;
  }

  return (
    <DataGrid dataSource={normalRows} showBorders wordWrapEnabled height={650}>
      <Scrolling mode="virtual" />

      {/* ===== COLUMNAS ===== */}
      {visibleColumns.map((col) => (
        <Column
          key={col}
          dataField={col}
          caption={columnCaptionMap[col] ?? col}
          alignment={col.includes('IMPORTE') || col === 'HORAS' ? 'right' : 'left'}
        />
      ))}

      {/* ===== TOTALES ===== */}
      <Summary>
        {totalsRow &&
          visibleColumns.map((col) => (
            <TotalItem key={col} column={col} summaryType="custom" displayFormat={col === 'EJERCICIO' ? 'TOTAL' : (totalsRow[col] ?? '')} />
          ))}
      </Summary>
    </DataGrid>
  );
};
